/**
 * Both CAM formats store text as UTF-16LE runs inside binary streams, and name
 * their tools in free text. `classifyTool` turns that text into the same
 * bitType labels FileFinity's bit catalog uses, so a suggestion can be matched.
 */

/** Build a string from char codes without an unbounded spread: a single
 * `String.fromCharCode(...run)` throws RangeError once the run exceeds the
 * engine's argument cap, and these runs come from arbitrary binary files. */
export function charsToString(run: number[]): string {
  const CHUNK = 4096;
  let s = '';
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < run.length; i += CHUNK) {
    s += String.fromCharCode(...run.slice(i, i + CHUNK));
  }
  return s;
}

/** UTF-16LE runs of >= min printable chars. The high byte is 0 and the low byte
 * is printable-ASCII *or* latin1 (0xa0-0xff) so names do not fragment at a
 * degree sign or fraction glyph. */
export function utf16Strings(b: Uint8Array, min = 3): string[] {
  const out: string[] = [];
  let run: number[] = [];
  const flush = () => {
    if (run.length >= min) out.push(charsToString(run));
    run = [];
  };
  for (let i = 0; i + 1 < b.length; i += 2) {
    const lo = b[i];
    const hi = b[i + 1];
    const printable = hi === 0 && ((lo >= 0x20 && lo <= 0x7e) || lo >= 0xa0);
    if (printable) run.push(lo);
    else flush();
  }
  flush();
  return out;
}

export function asciiStrings(b: Uint8Array, min = 4): string[] {
  const out: string[] = [];
  let run: number[] = [];
  const flush = () => {
    if (run.length >= min) out.push(charsToString(run));
    run = [];
  };
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < b.length; i++) {
    const ch = b[i];
    if (ch >= 0x20 && ch <= 0x7e) run.push(ch);
    else flush();
  }
  flush();
  return out;
}

type Label = 'dia' | 'rad' | 'other';
type Dim = { inches: number; label: Label | null; chained: boolean };

/** A length: a mixed fraction ("1-1/4", "1 1/4"), a fraction, or a number with
 * a unit. A unitless integer is a flute count or part number, never a length. */
const DIM =
  /(?<![\w./])(?:(\d+)[-\s](\d+)\s*\/\s*(\d+)|(\d+)\s*\/\s*(\d+)|(\d*\.?\d+))\s*("|inch(?:es)?\b|in\b|mm\b)?/gi;
const OTHER_LABEL =
  /^\W*(?:shank|loc\b|cut(?:ting)? length|flute length|cutting edge|length|long\b|oal\b|reach)/i;
const DIA_LABEL = /^\W*(?:dia(?:meter)?\b|ø)/i;
const OTHER_PREFIX =
  /\b(?:shank|loc|cut(?:ting)? length|flute length|cutting edge|length|long|oal|reach)\W*$/i;
const DIA_PREFIX = /(?:\bdia(?:meter)?|ø)\W*$/i;
// \b keeps the r ending "Router" or "Cutter" from reading as a radius.
const RAD_LABEL = /^\W*(?:radius|rad|r)\b/i;
const RAD_PREFIX = /\b(?:radius|rad|r)\W*$/i;
// On a form tool, "(27/40\")" is the profile width, not the cutting diameter.
const BRACKET_FRACTION = /\(\s*(?:\d+[-\s])?\d+\s*\/\s*\d+\s*"?\s*\)/g;

type Hit = { label: Label; at: number; end: number };

const SUFFIXES: [Label, RegExp][] = [
  ['other', OTHER_LABEL],
  ['dia', DIA_LABEL],
  ['rad', RAD_LABEL],
];
const PREFIXES: [Label, RegExp][] = [
  ['other', OTHER_PREFIX],
  ['dia', DIA_PREFIX],
  ['rad', RAD_PREFIX],
];

/** The label word at the start of the text after a size. */
function suffixLabel(g: string): Hit | null {
  const hit = SUFFIXES.map(([label, re]) => ({ label, m: g.match(re) })).find(
    (x) => x.m
  );
  return hit?.m ? { label: hit.label, at: 0, end: hit.m[0].length } : null;
}

/** The label word at the end of the text before a size. */
function prefixLabel(g: string): Hit | null {
  const hit = PREFIXES.map(([label, re]) => ({ label, m: g.match(re) })).find(
    (x) => x.m
  );
  return hit?.m
    ? { label: hit.label, at: hit.m.index ?? 0, end: g.length }
    : null;
}

const ownsPrefix = (
  pre: Hit | null,
  prevSuf: Hit | null,
  prevPrefixed: boolean
): pre is Hit =>
  pre !== null && !(prevSuf && pre.at < prevSuf.end && !prevPrefixed);

const ownsSuffix = (
  suf: Hit | null,
  nextPre: Hit | null,
  preOwn: boolean
): suf is Hit => suf !== null && !(nextPre && nextPre.at < suf.end && preOwn);

/**
 * The label a size takes from the text around it. One word between two sizes
 * ("Shank 1/4\" Dia 1/2\"") labels only one of them: the size before it,
 * unless that size is already labelled by the word before it, in which case
 * the name is written label-first.
 */
function labelFor(
  before: string,
  after: string,
  isFirst: boolean,
  isLast: boolean,
  prevPrefixed: boolean
): { label: Dim['label']; prefixed: boolean } {
  const pre = prefixLabel(before);
  const preOwn = ownsPrefix(
    pre,
    isFirst ? null : suffixLabel(before),
    prevPrefixed
  );
  const suf = suffixLabel(after);
  const sufOwn = ownsSuffix(suf, isLast ? null : prefixLabel(after), preOwn);
  // A following label beats a preceding one: it is the long-standing
  // manufacturer convention, so a size labelled both ways reads the suffix.
  let label: Dim['label'] = null;
  if (sufOwn) label = suf.label;
  else if (preOwn) label = pre.label;
  return { label, prefixed: preOwn };
}

/** A size match in inches; null for a bare number with no unit. */
function inchesOf(m: RegExpMatchArray): number | null {
  const [, whole, wn, wd, n, d, num, unit] = m;
  if (num !== undefined && unit === undefined) return null;
  let v = Number(num);
  if (wd !== undefined) v = Number(whole) + Number(wn) / Number(wd);
  else if (d !== undefined) v = Number(n) / Number(d);
  return unit?.toLowerCase() === 'mm' ? v / 25.4 : v;
}

function dims(name: string): Dim[] {
  const hits = [...name.matchAll(DIM)];
  const gap = (i: number): string =>
    name.slice(
      i === 0 ? 0 : (hits[i - 1].index ?? 0) + hits[i - 1][0].length,
      hits[i]?.index ?? name.length
    );
  const out: Dim[] = [];
  let prevPrefixed = false;
  hits.forEach((m, i) => {
    const after = gap(i + 1);
    const { label, prefixed } = labelFor(
      gap(i),
      after,
      i === 0,
      i + 1 === hits.length,
      prevPrefixed
    );
    prevPrefixed = prefixed;
    const inches = inchesOf(m);
    if (inches === null) return;
    out.push({ inches, label, chained: /^\s*x\s*$/i.test(after) });
  });
  return out;
}

function labelledDia(all: Dim[], formTool: boolean): number | null {
  const dia = all.find((x) => x.label === 'dia')?.inches;
  if (dia !== undefined) return dia;
  // Diameter beats radius: on bowl bits the radius is the corner, not width/2.
  const rad = formTool ? undefined : all.find((x) => x.label === 'rad')?.inches;
  return rad === undefined ? null : rad * 2;
}

function unlabelledDia(all: Dim[]): number | null {
  const unlabelled = all.filter((x) => x.label === null);
  const [first] = unlabelled;
  if (!first) return null;
  const agree = unlabelled.every(
    (x) => Math.abs(x.inches - first.inches) <= 0.06 * x.inches
  );
  return agree || first.chained ? first.inches : null;
}

/**
 * Cutting diameter in inches from a tool name, or null.
 *
 * Rule: a size labelled by the word after (1/4" Dia) or before (Dia 1/4") it
 * wins. A size labelled radius, rad or R (2x) counts only when no size is
 * labelled Dia. On a form tool only a Dia-labelled size counts. Sizes
 * labelled shank, LOC, cutting/flute length or long are other dimensions and
 * dropped.
 * Of what is left, one size, or several within 6% (a 1/4" beside its rounded
 * 6mm), is the diameter. Several that disagree are ambiguous, unless the first heads an
 * "A x B" chain: manufacturers write Dia x LOC x Shank, diameter first. A wrong
 * diameter mis-matches the bit, so anything else is null. Sizes above 3" (the
 * largest common surfacing bit) are mis-parses.
 */
function diaFromName(name: string, formTool: boolean): number | null {
  const all = dims(name);
  const d =
    labelledDia(all, formTool) ?? (formTool ? null : unlabelledDia(all));
  if (d === null || !(d > 0) || d > 3) return null;
  return Math.round(d * 1e4) / 1e4;
}

export type BitFamily = 'endmill';

// fallow-ignore-next-line complexity
export function classifyTool(name: string | null): {
  bitType: string | null;
  dia: number | null;
  vAngle: number | null;
  /** Set only when the name gives the family but not the bitType within it. */
  bitFamily?: BitFamily;
} {
  if (!name) return { bitType: null, dia: null, vAngle: null };
  const raw = name.trim();
  const low = raw.toLowerCase();
  // Repair a common OLE mis-decode: a leading '!' where a '1' belongs.
  const fixed = raw.replace(/^!(?=[\d/.])/, '1');

  // Vectric writes the angle with a decimal — "V-Bit (60.0° - 1/4\")" — so the
  // fraction has to be consumed, or nothing matches and the bit loses its angle.
  const angMatch = raw.match(/(\d{2,3})(?:\.\d+)?\s*[-\s]*(?:°|deg|degree)/i);
  const vAngle = angMatch ? Number(angMatch[1]) : null;

  const has = (...k: string[]) => k.some((s) => low.includes(s));
  const formTool = has('roundover', 'round over', 'ogee', 'form tool');
  let bitType: string | null;
  if (has('tapered', 'conical', 'skinny jenny', 'tbn'))
    bitType = 'Tapered Ball Nose';
  else if (has('ball')) bitType = 'Ball Nose';
  else if (
    vAngle !== null ||
    has(
      'v-groove',
      'vgroove',
      'v groove',
      'groovee',
      'v-bit',
      'vbit',
      '-degree v',
      'degree v'
    )
  )
    bitType = 'V Bit';
  else if (has('compression')) bitType = 'Compression Bit';
  else if (low.includes('down') && has('cut', 'dnc', 'shear'))
    bitType = 'Downcut Endmill';
  else if (low.includes('up') && low.includes('cut')) bitType = 'Upcut Endmill';
  else if (has('bowl')) bitType = 'Bowl';
  else if (formTool) bitType = 'Ogee / Roundover';
  else if (has('drill')) bitType = 'Drill Mill';
  else if (has('surfac', 'spoilboard', 'slab', 'fly cut', 'flycut'))
    bitType = 'Surfacing';
  // A directionless end mill/spiral/straight is null, not a guessed direction,
  // so the wizard's bit hint shows no type: a usually-wrong hint is worse.
  else bitType = null;
  const bitFamily: BitFamily | undefined =
    bitType === null &&
    has('end mill', 'endmill', 'end-mill', 'spiral', 'straight')
      ? 'endmill'
      : undefined;

  // Form-tool names give the profile radius, labelled or not ('Ogee 6 mm' is
  // 32 mm across in Carveco's library); the diameter needs an unnamed innerDia.
  const dia = formTool
    ? diaFromName(fixed.replace(BRACKET_FRACTION, ' '), true)
    : diaFromName(fixed, false);
  return { bitType, dia, vAngle, bitFamily };
}
